import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Mail, Plus, Gift, Calendar, Heart, LogIn, UserPlus, Send, UserCircle, X, Check, Package, AlertCircle, Clock, ShoppingBag, Menu, LogOut } from 'lucide-react';
import { CardSelector } from './components/CardSelector';
import { SignatureUpload } from './components/SignatureUpload';
import { EditProfile } from './components/EditProfile';
import { ContactForm } from './components/ContactForm';
import { ResetPassword } from './components/ResetPassword';
import { useAuth } from './hooks/useAuth';
import { getContacts, addContact, createOrder, deleteContact } from './lib/db';
import { supabase } from './lib/supabase';
import { EditContact } from './components/EditContact';
import { BulkCardSender } from './components/BulkCardSender';
import { CreditBundles } from './components/CreditBundles';
import { getSubscriptionStatus, handleStripeReturn, createCheckoutSession, redirectToCustomerPortal, hasActiveSubscription } from './lib/stripe';
import { UpcomingEvents } from './components/UpcomingEvents';
import { SubscriptionTiers } from './components/SubscriptionTiers';
import { CreditDetailsModal } from './components/CreditDetailsModal';
import { OrderHistoryModal } from './components/OrderHistoryModal';
import { OccasionSelectorModal } from './components/modals/OccasionSelectorModal';
import { CardOccasion } from './types/cards';
import TemplateTest from './pages/TemplateTest';
import ResetPasswordPage from './pages/ResetPassword';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Contact } from './types';
import { SubscriptionStatus } from './types/subscription';
import { UserProfile } from './types/user';

interface CardOrder {
  id: string;
  lob_order_id: string;
  status: string;
  expected_delivery_date: string;
  credits_used: number;
  recipient_name: string;
  recipient_address: string;
  created_at: string;
  contact_id?: string;
  card_id?: string;
  message?: string;
}

interface OrderHistoryResponse {
  data: CardOrder[];
  total_count: number;
}

interface EditProfileProps {
  onClose: () => void;
  onUpdate: () => void;
}

interface CreditPurchase {
  id: string;
  credits_amount: number;
  credits_remaining: number;
  purchase_date: string;
  expiration_date: string;
  days_remaining: number;
}

interface UserCreditsInfo {
  total_credits: number;
  purchases: CreditPurchase[];
}

// Interface for card order parameters
interface CardOrderParams {
  recipientName: string;
  occasion: string;
  contactId?: string;
  renderMode?: string;
  message: string;
  recipientAddress: any;
  cardImage: string;
  signatureUrl?: string | null;
  textStyle?: any;
  frontImageUrl?: string;
  backImageUrl?: string;
}

function Sidebar({ 
  isOpen, 
  onClose,
  setShowProfileEdit,
  setShowOrderHistory,
  fetchOrderHistory,
  setShowCreditBundles,
  logout
}: { 
  isOpen: boolean; 
  onClose: () => void;
  setShowProfileEdit: (show: boolean) => void;
  setShowOrderHistory: (show: boolean) => void;
  fetchOrderHistory: () => void;
  setShowCreditBundles: (show: boolean) => void;
  logout: () => void;
}) {
  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 transform transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold font-display text-brand-secondary">Menu</h2>
            <button
              onClick={onClose}
              className="text-brand-secondary hover:text-brand-primary"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <nav className="p-4 space-y-2">
          <button
            onClick={() => {
              setShowProfileEdit(true);
              onClose();
            }}
            className="w-full flex items-center gap-2 text-brand-primary hover:text-brand-secondary transition-colors p-2"
          >
            <UserCircle className="w-5 h-5" />
            <span>Edit Profile</span>
          </button>
          <button
            onClick={() => {
              setShowOrderHistory(true);
              fetchOrderHistory();
              onClose();
            }}
            className="w-full flex items-center gap-2 text-brand-primary hover:text-brand-secondary transition-colors p-2"
          >
            <ShoppingBag className="w-5 h-5" />
            <span>Order History</span>
          </button>
          <button
            onClick={() => {
              setShowCreditBundles(true);
              onClose();
            }}
            className="w-full flex items-center gap-2 text-brand-primary hover:text-brand-secondary transition-colors p-2"
          >
            <Package className="w-5 h-5" />
            <span>Buy Credits</span>
          </button>
          <button
            onClick={() => {
              logout();
              onClose();
            }}
            className="w-full flex items-center gap-2 text-brand-secondary hover:text-brand-primary transition-colors p-2"
          >
            <LogOut className="w-5 h-5" />
            <span>Sign Out</span>
          </button>
        </nav>
      </div>
    </>
  );
}

export default function App() {
  const { user, authError, signIn, signUp, logout, resetPassword, resetEmailSent, setResetEmailSent } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showSignatureUpload, setShowSignatureUpload] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedOccasion, setSelectedOccasion] = useState<CardOccasion | null>(null);
  const [showOccasionSelector, setShowOccasionSelector] = useState(false);
  const [showCardSelector, setShowCardSelector] = useState(false);
  const [cardOrders, setCardOrders] = useState<CardOrder[]>([]);
  const [showCreditBundles, setShowCreditBundles] = useState(false);
  const [userCredits, setUserCredits] = useState<number>(0);
  const [creditDetails, setCreditDetails] = useState<CreditPurchase[]>([]);
  const [showCreditDetails, setShowCreditDetails] = useState(false);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [orderHistory, setOrderHistory] = useState<CardOrder[]>([]);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [orderHistoryPage, setOrderHistoryPage] = useState(0);
  const [orderHistoryTotal, setOrderHistoryTotal] = useState(0);
  const ORDER_HISTORY_LIMIT = 5;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showUpcomingEvents, setShowUpcomingEvents] = useState(false);
  const [showSubscriptionTiers, setShowSubscriptionTiers] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>({
    subscription: {},
    freeCards: 0,
    cardsUsedThisMonth: 0
  });
  const [error, setError] = useState<string | null>(null);

  // 1. Basic utility functions (no dependencies on other functions)
  
  // Add this helper function to get the error message from Supabase Function error
  function extractErrorDetails(error: any) {
    if (!error) return null;
    try {
      // Handle different error formats
      if (typeof error === 'string') {
        if (error.includes('{')) {
          const jsonStart = error.indexOf('{');
          const jsonEnd = error.lastIndexOf('}') + 1;
          const jsonString = error.substring(jsonStart, jsonEnd);
          return JSON.parse(jsonString);
        }
      } else if (error.message && typeof error.message === 'string') {
        if (error.message.includes('{')) {
          const jsonStart = error.message.indexOf('{');
          const jsonEnd = error.message.lastIndexOf('}') + 1;
          const jsonString = error.message.substring(jsonStart, jsonEnd);
          return JSON.parse(jsonString);
        }
      }
    } catch (e) {
      console.error('Error parsing error details:', e);
    }
    return null;
  }
  
  // Function to resize images for Lob
  async function resizeImageForLob(imageUrl: string) {
    return new Promise((resolve, reject) => {
      // Create an image to load the original
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      
      img.onload = () => {
        try {
          // Required dimensions for Lob (6.25 × 4.25 inches at 300dpi)
          const targetWidth = 1875;
          const targetHeight = 1275;
          
          // Check if resizing is needed
          if (img.width >= targetWidth && img.height >= targetHeight) {
            console.log('Image already meets size requirements:', img.width, 'x', img.height);
            resolve(imageUrl); // Use original image
            return;
          }
          
          // Create a canvas to resize the image
          const canvas = document.createElement('canvas');
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          
          // Get canvas context and fill with white background
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          
          // Fill with white background
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, targetWidth, targetHeight);
          
          // Calculate dimensions while preserving aspect ratio
          let newWidth, newHeight;
          const originalAspect = img.width / img.height;
          const targetAspect = targetWidth / targetHeight;
          
          if (originalAspect > targetAspect) {
            // Original is wider than target
            newWidth = targetWidth;
            newHeight = targetWidth / originalAspect;
          } else {
            // Original is taller than target
            newHeight = targetHeight;
            newWidth = targetHeight * originalAspect;
          }
          
          // Calculate centering position
          const x = (targetWidth - newWidth) / 2;
          const y = (targetHeight - newHeight) / 2;
          
          // Draw the image centered on the canvas
          ctx.drawImage(img, x, y, newWidth, newHeight);
          
          // Convert to data URL
          const resizedImageUrl = canvas.toDataURL('image/jpeg', 0.95);
          console.log(`Resized image from ${img.width}x${img.height} to ${targetWidth}x${targetHeight}`);
          
          resolve(resizedImageUrl);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = (error) => {
        reject(new Error(`Failed to load image: ${error}`));
      };
      
      // Start loading the image
      img.src = imageUrl;
    });
  }
  
  // Notification helper
  const showNotification = (message: string, type = 'success', duration = 5000) => {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 z-50 px-4 py-3 rounded-lg ${
      type === 'success' 
        ? 'bg-green-50 border border-green-200 text-green-700' 
        : 'bg-red-50 border border-red-200 text-red-700'
    }`;
    notification.innerHTML = message;
    
    // Add to document and remove after duration
    document.body.appendChild(notification);
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, duration);
  };
  
  // 2. Data fetching functions
  
  // Fetch contacts function
  async function fetchContacts() {
    if (!user) return;
    try {
      const contacts = await getContacts(user.id);
      setContacts(contacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }
  
  // Fetch user credits function
  async function fetchUserCredits() {
    try {
      const { data, error } = await supabase.rpc('get_user_credits_with_expiration');
      
      if (error) throw error;
      
      if (data && Array.isArray(data) && data.length > 0) {
        const creditInfo = data[0];  // Get the first object from the array
        setUserCredits(creditInfo.total_credits || 0);
        setCreditDetails(creditInfo.purchases || []);
        console.log('Credits updated:', creditInfo.total_credits, creditInfo.purchases);
      }
    } catch (error) {
      console.error('Error fetching user credits:', error);
    }
  }
  
  // Fetch order history function
  async function fetchOrderHistory(page = 0) {
    try {
      setOrderHistoryLoading(true);
      const offset = page * ORDER_HISTORY_LIMIT;
      
      const { data, error } = await supabase.rpc(
        'get_user_orders',
        {
          p_limit: ORDER_HISTORY_LIMIT,
          p_offset: offset
        }
      );
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setOrderHistory(data);
        setOrderHistoryTotal(data[0].total_count || 0);
      } else {
        setOrderHistory([]);
        setOrderHistoryTotal(0);
      }
    } catch (error) {
      console.error('Error fetching order history:', error);
    } finally {
      setOrderHistoryLoading(false);
    }
  }
  
  // Fetch subscription status
  async function fetchSubscriptionStatus() {
    try {
      const status = await getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  }
  
  // Handle Stripe return
  async function handleStripeReturn() {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const sessionId = params.get('session_id');
    const subscription = params.get('subscription');
    
    if (success === 'true' && sessionId) {
      try {
        // Use fetch directly with URL parameters instead of body
        const { data, error } = await supabase.functions.invoke(`stripe-check-session?session_id=${sessionId}`);
        
        if (error) throw error;
        
        // Clean URL parameters
        const url = new URL(window.location.href);
        url.search = '';
        window.history.replaceState({}, document.title, url.toString());
        
        return { 
          success: true, 
          sessionId, 
          status: data,
          isSubscription: subscription === 'true'
        };
      } catch (error) {
        console.error('Error checking session:', error);
        return { success: false, error };
      }
    } else if (params.get('canceled')) {
      // Clean URL parameters
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, document.title, url.toString());
      
      return { success: false, canceled: true };
    }
    
    return null;
  }
  
  // 3. Refresh functions that depend on fetch functions
  
  // Helper function to refresh order history
  const refreshOrderHistory = async () => {
    try {
      await fetchOrderHistory(orderHistoryPage);
    } catch (error) {
      console.error('Error refreshing order history:', error);
    }
  };
  
  // Helper function to refresh user subscription
  const refreshUserSubscription = async () => {
    try {
      const status = await getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('Error refreshing subscription status:', error);
    }
  };
  
  // 4. Define card processing functions
  
  // Card processing function
  const processCardOrder = async ({
    recipientName,
    occasion,
    contactId,
    renderMode = 'render images',
    message,
    recipientAddress,
    cardImage,
    signatureUrl,
    textStyle,
    frontImageUrl,
    backImageUrl
  }: CardOrderParams) => {
    try {
      console.log('Processing card order with data:', {
        recipientName,
        occasion,
        contactId,
        renderMode,
        hasImageUrls: !!frontImageUrl && !!backImageUrl
      });
      
      // Check necessary data is available
      if (!message && renderMode !== 'remote_urls') {
        return {
          success: false,
          error: 'Message is required for card order'
        };
      }
      
      // For remote URLs mode, we need to have both image URLs
      if (renderMode === 'remote_urls' && (!frontImageUrl || !backImageUrl)) {
        console.error('Missing image URLs for remote_urls mode:', { frontImageUrl, backImageUrl });
        return {
          success: false,
          error: 'Front and back image URLs are required for remote_urls mode'
        };
      }
      
      try {
        let orderResult;
        
        // Create order based on mode
        if (renderMode === 'remote_urls' && frontImageUrl && backImageUrl) {
          console.log('Using image URLs for create-lob-order:', { 
            front: frontImageUrl.substring(0, 80) + '...', 
            back: backImageUrl.substring(0, 80) + '...' 
          });
          
          // Make direct API call to create-lob-order
          const response = await supabase.functions.invoke('create-lob-order', {
            body: {
              description: `${occasion} card for ${recipientName}`,
              to: {
                name: recipientName,
                address_line1: recipientAddress?.address_line1 || '',
                address_city: recipientAddress?.address_city || '',
                address_state: recipientAddress?.address_state || '',
                address_zip: recipientAddress?.address_zip || '',
                address_country: recipientAddress?.address_country || 'US'
              },
              front: frontImageUrl, 
              back: backImageUrl,
              size: "4x6",
              useRemoteUrls: true
            }
          });
          
          if (response.error) {
            console.error('Error from create-lob-order Edge Function:', response.error);
            throw new Error(`Error from Edge Function: ${response.error.message || 'Unknown error'}`);
          }
          
          console.log('Card order succeeded:', response.data);
          orderResult = response.data;
        } else {
          // Call create order with rendering parameters
          console.log('Using render mode for order - will generate templates server-side');
          const response = await createOrder({
            contactId,
            message,
            occasion: (occasion || 'general') as CardOccasion,
            imageUrl: cardImage,
            to: {
              name: recipientName,
              address_line1: recipientAddress?.address_line1 || '',
              address_city: recipientAddress?.address_city || '',
              address_state: recipientAddress?.address_state || '',
              address_zip: recipientAddress?.address_zip || '',
              address_country: recipientAddress?.address_country || 'US'
            },
            front: "", // These will be generated server-side
            back: "",
            description: `${occasion} card for ${recipientName}`,
            size: "4x6" // Explicitly set to 4x6 for Lob API
          });
          
          orderResult = response;
        }
        
        // Add record to orders table
        if (contactId) {
          const { error: dbError } = await supabase
            .from('orders')
            .insert({
              user_id: user?.id,
              contact_id: contactId,
              lob_order_id: orderResult.id,
              type: 'card',
              status: 'processing'
            });
          
          if (dbError) {
            console.error('Error saving order to database:', dbError);
          }
        }
        
        // Refresh user credits after order
        await fetchUserCredits();
        
        return {
          success: true,
          orderId: orderResult.id,
          lobOrderId: orderResult.id
        };
      } catch (error: any) {
        // Check for specific error types
        if (error.message && error.message.includes('402')) {
          console.error('402 Payment Required error:', error);
          showNotification('Payment required. Please purchase more credits.', 'error', 7000);
          return {
            success: false,
            error: 'Payment required. Please purchase more credits.'
          };
        }
        
        console.error('Error processing card order:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error processing card order:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error processing card order'
      };
    }
  };
  
  // Update the checkStripeReturn function to avoid circular dependencies
  const checkStripeReturn = useCallback(async () => {
    // Check if a Stripe checkout process is returning
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const sessionId = params.get('session_id');
    const hasPendingCard = params.get('pendingCard') === 'true';
    const timestamp = params.get('timestamp');
    
    if (!success || !sessionId) return; // Not a Stripe return
    
    try {
      // Prevent multiple processing attempts by setting a flag in sessionStorage
      const processingKey = `cardible_processing_${sessionId}`;
      if (sessionStorage.getItem(processingKey)) {
        console.log('Already processing this Stripe return. Skipping duplicate processing attempt.');
        return;
      }
      sessionStorage.setItem(processingKey, 'true');
      
      // Get the result of the Stripe checkout
      const stripeReturn = await handleStripeReturn();
      console.log('Stripe checkout result:', stripeReturn);
      
      if (stripeReturn?.success) {
        console.log('Stripe purchase successful:', stripeReturn);
        
        // Add notification that order processing is starting
        showNotification('Processing your card order. Please wait...', 'success', 5000);
        
        // Look for pending card data - check both possible keys
        const pendingCardDataKey = 'cardible_pending_card';
        let pendingCardData = localStorage.getItem(pendingCardDataKey);
        
        if (!pendingCardData) {
          const pendingCardId = params.get('pendingCard');
          if (pendingCardId) {
            pendingCardData = localStorage.getItem(`pendingCard_${pendingCardId}`);
          }
        }
        
        if (!pendingCardData) {
          console.error('No pending card data found in localStorage');
          showNotification('Could not find your card data. Please try sending your card again.', 'error');
          sessionStorage.removeItem(processingKey);
          return;
        }
        
        // Parse the card data first to check if it's valid
        let parsedCardData;
        try {
          parsedCardData = JSON.parse(pendingCardData);
          console.log('Found pending card data:', parsedCardData);
        } catch (parseError) {
          console.error('Error parsing stored card data:', parseError);
          showNotification('Invalid card data found. Please try sending your card again.', 'error');
          sessionStorage.removeItem(processingKey);
          return;
        }
        
        // Wait longer to ensure credits are applied in the database
        console.log('Waiting for credits to be applied before processing card...');
        await new Promise(resolve => setTimeout(resolve, 3500));
        
        // Refresh credits to ensure we have the latest data
        await fetchUserCredits();
        
        let authRetries = 5;
        let authSuccess = false;
        
        while (authRetries > 0 && !authSuccess) {
          // Use different methods to check auth
          const isAuthenticated = !!user || !!(await supabase.auth.getUser()).data.user;
          
          if (isAuthenticated) {
            authSuccess = true;
            console.log('User authentication confirmed');
          } else {
            console.log(`Auth check failed. Retries left: ${authRetries}`);
            authRetries--;
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
        
        // Don't require user to be authenticated if we have all necessary data for the Edge Function
        if (parsedCardData.frontImageUrl && parsedCardData.backImageUrl && parsedCardData.recipientAddress) {
          // We can proceed with direct API call even if user auth isn't available
          console.log('Attempting direct Edge Function call with pre-rendered images');
          
          try {
            // Get auth session directly (might work even if user object isn't loaded)
            const sessionResponse = await supabase.auth.getSession();
            if (sessionResponse.data && sessionResponse.data.session) {
              // Direct invoke with session
              console.log('Got auth session, directly invoking create-lob-order...');
              
              const { data: lobData, error: lobError } = await supabase.functions.invoke('create-lob-order', {
                body: {
                  description: `${parsedCardData.occasion} card for ${parsedCardData.recipientName}`,
                  to: parsedCardData.recipientAddress,
                  front: parsedCardData.frontImageUrl,
                  back: parsedCardData.backImageUrl,
                  size: '4x6',
                  useRemoteUrls: true
                }
              });
              
              if (lobError) {
                console.error('Error in direct create-lob-order call:', lobError);
                throw lobError;
              }
              
              console.log('Card successfully sent via direct Edge Function call:', lobData);
              showNotification('Your physical card has been scheduled for delivery!', 'success');
              localStorage.removeItem(pendingCardDataKey);
              
              // Clean URL parameters
              const url = new URL(window.location.href);
              url.search = '';
              window.history.replaceState({}, document.title, url.toString());
              
              // Refresh user's credit balance and data
              await fetchUserCredits();
              await refreshOrderHistory();
              
              // We're done!
              sessionStorage.removeItem(processingKey);
              return;
            }
          } catch (directError) {
            console.error('Error in direct Edge Function call:', directError);
            // Continue to fallback methods
          }
        }
        
        // If user authentication was successful, process through standard flow
        if (authSuccess) {
          let maxTries = 3;
          let tryCount = 0;
          let success = false;
          
          while (!success && tryCount < maxTries) {
            tryCount++;
            try {
              console.log(`Processing attempt ${tryCount}: Using auth to process card`);
              
              // Process the card with appropriate method based on data
              if (parsedCardData.frontImageUrl && parsedCardData.backImageUrl) {
                // Use processCardOrder with remote URLs
                const orderResult = await processCardOrder({
                  recipientName: parsedCardData.recipientName,
                  occasion: parsedCardData.occasion,
                  contactId: parsedCardData.contactId,
                  renderMode: 'remote_urls',
                  message: parsedCardData.message || "Your special message",
                  recipientAddress: parsedCardData.recipientAddress,
                  cardImage: parsedCardData.cardImage,
                  signatureUrl: parsedCardData.signatureUrl,
                  textStyle: parsedCardData.textStyle,
                  frontImageUrl: parsedCardData.frontImageUrl,
                  backImageUrl: parsedCardData.backImageUrl
                });
                
                if (orderResult?.success) {
                  console.log('Card successfully processed via processCardOrder');
                  showNotification('Your physical card has been scheduled for delivery!', 'success');
                  success = true;
                  localStorage.removeItem(pendingCardDataKey);
                } else {
                  throw new Error(orderResult?.error || 'Failed to process card order');
                }
              } else {
                // Use completePendingCardOrder for non-prerendered images
                const processed = await completePendingCardOrder(parsedCardData);
                
                if (processed) {
                  console.log('Successfully processed pending card order');
                  localStorage.removeItem(pendingCardDataKey);
                  success = true;
                } else {
                  throw new Error('Failed to process pending card order');
                }
              }
            } catch (processError) {
              console.error(`Attempt ${tryCount} failed:`, processError);
              
              if (tryCount < maxTries) {
                console.log(`Waiting before retry ${tryCount + 1}...`);
                await new Promise(resolve => setTimeout(resolve, 2000));
                
                // Refresh credits again before retrying
                await fetchUserCredits();
              } else {
                console.error('Maximum retry attempts reached');
                showNotification('We had trouble sending your card. Please try manually sending it from your dashboard.', 'error', 7000);
              }
            }
          }
        } else {
          console.error('Unable to authenticate user after multiple attempts');
          showNotification('Authentication failed. Please refresh the page or try manually sending your card from the dashboard.', 'error', 10000);
        }
        
        // Clean URL parameters
        const url = new URL(window.location.href);
        url.search = '';
        window.history.replaceState({}, document.title, url.toString());
        
        // Refresh user's credit balance and data
        await fetchUserCredits();
        await refreshOrderHistory();
      }
      
      // Remove the processing flag after completion
      sessionStorage.removeItem(`cardible_processing_${sessionId}`);
    } catch (error) {
      console.error('Error handling Stripe return:', error);
      showNotification('There was an issue completing your order. Please try again or contact support.', 'error');
      // Remove the processing flag so it can be retried
      sessionStorage.removeItem(`cardible_processing_${sessionId}`);
    }
  }, [user, fetchUserCredits, refreshOrderHistory]);
  
  // Complete pending card order function
  const completePendingCardOrder = async (cardData: any) => {
    try {
      console.log('Attempting to process pending card with data:', cardData);
      
      if (!user) {
        throw new Error('User not available to process card order');
      }
      
      // Find matching contact if available
      let contactId: string | undefined = undefined;
      
      if (cardData.recipientName && cardData.recipientAddress) {
        try {
          const contacts = await getContacts(user.id);
          const matchingContact = contacts.find(contact => 
            contact.name === cardData.recipientName && 
            contact.address_line1 === cardData.recipientAddress.address_line1
          );
          
          if (matchingContact) {
            console.log('Found matching contact ID:', matchingContact.id);
            contactId = matchingContact.id;
          }
        } catch (contactError) {
          console.error('Error fetching contacts:', contactError);
          // Continue without contact ID
        }
      }

      console.log('Processing pending card order with image URL:', cardData.cardImage);
      
      // Check if we have already rendered images stored in the card data
      if (cardData.frontImageUrl && cardData.backImageUrl) {
        console.log('Using pre-rendered images from card data:', {
          front: cardData.frontImageUrl,
          back: cardData.backImageUrl
        });
        
        try {
          // Add credit check and error handling for 402 errors with retry mechanism
          const maxRetries = 5;
          let retryCount = 0;
          let totalCredits = 0;
          
          // Try to get updated credit balance with retries
          while (retryCount < maxRetries) {
            try {
              // Wait longer between retries
              if (retryCount > 0) {
                console.log(`Retry ${retryCount}/${maxRetries} for credit check...`);
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
              
              // Check if user has available credits
              const { data: creditData, error } = await supabase.rpc('get_user_credits_with_expiration');
              
              if (error) {
                if (error.code === 'PGRST002' || error.message.includes('503')) {
                  console.log('Database temporarily unavailable. Retrying...');
                  retryCount++;
                  continue;
                } else {
                  throw error;
                }
              }
              
              // Successfully retrieved credit data
              totalCredits = creditData && creditData.length > 0 ? creditData[0].total_credits || 0 : 0;
              console.log(`Credit check successful. Available credits: ${totalCredits}`);
              break;
            } catch (creditError) {
              console.error('Error checking credits:', creditError);
              retryCount++;
              if (retryCount >= maxRetries) {
                throw new Error('Failed to verify credit balance after multiple attempts');
              }
            }
          }
          
          if (totalCredits <= 0) {
            showNotification('You don\'t have enough credits to send this card. Please purchase more credits.', 'error', 7000);
            throw new Error('Insufficient credits');
          }
          
          // Process the card order using the stored image URLs
          const orderResult = await processCardOrder({
            recipientName: cardData.recipientName,
            occasion: cardData.occasion,
            contactId: contactId || cardData.contactId,
            renderMode: 'remote_urls',
            message: cardData.message,
            recipientAddress: cardData.recipientAddress,
            cardImage: cardData.cardImage,
            signatureUrl: cardData.signatureUrl,
            textStyle: cardData.textStyle
          });
          
          if (orderResult?.success) {
            console.log('Card order processed successfully:', orderResult);
            showNotification('Your physical card has been scheduled for delivery.', 'success');
            
            // Refresh order history and credits
            refreshOrderHistory();
            fetchUserCredits();
            return true;
          } else {
            throw new Error(orderResult?.error || 'Unknown error processing card order');
          }
        } catch (error: any) {
          // Check for 402 Payment Required error
          if (error.message && error.message.includes('402')) {
            showNotification('Payment required. Please ensure you have enough credits or try again.', 'error', 7000);
          } else {
            throw error;
          }
          return false;
        }
      } else {
        // Process the card order using the stored card data if no pre-rendered images
        // Use the same retry logic for checking credits
        try {
          // Add credit check with retry mechanism
          const maxRetries = 5;
          let retryCount = 0;
          let totalCredits = 0;
          
          // Try to get updated credit balance with retries
          while (retryCount < maxRetries) {
            try {
              if (retryCount > 0) {
                console.log(`Retry ${retryCount}/${maxRetries} for credit check...`);
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
              
              const { data: creditData, error } = await supabase.rpc('get_user_credits_with_expiration');
              
              if (error) {
                if (error.code === 'PGRST002' || error.message.includes('503')) {
                  console.log('Database temporarily unavailable. Retrying...');
                  retryCount++;
                  continue;
                } else {
                  throw error;
                }
              }
              
              totalCredits = creditData && creditData.length > 0 ? creditData[0].total_credits || 0 : 0;
              console.log(`Credit check successful. Available credits: ${totalCredits}`);
              break;
            } catch (creditError) {
              console.error('Error checking credits:', creditError);
              retryCount++;
              if (retryCount >= maxRetries) {
                throw new Error('Failed to verify credit balance after multiple attempts');
              }
            }
          }
          
          if (totalCredits <= 0) {
            showNotification('You don\'t have enough credits to send this card. Please purchase more credits.', 'error', 7000);
            throw new Error('Insufficient credits');
          }
          
          const orderResult = await processCardOrder({
            recipientName: cardData.recipientName,
            occasion: cardData.occasion,
            contactId: contactId || cardData.contactId,
            renderMode: 'render images', // Always use image rendering
            message: cardData.message,
            recipientAddress: cardData.recipientAddress,
            cardImage: cardData.cardImage,
            signatureUrl: cardData.signatureUrl,
            textStyle: cardData.textStyle
          });
          
          if (orderResult?.success) {
            // Show success message
            showNotification('Your physical card has been scheduled for delivery.', 'success');
            
            // Refresh order history and credits
            refreshOrderHistory();
            fetchUserCredits();
            return true;
          } else {
            throw new Error(orderResult?.error || 'Unknown error processing card order');
          }
        } catch (error: any) {
          // Check for 402 Payment Required error
          if (error.message && error.message.includes('402')) {
            showNotification('Payment required. Please ensure you have enough credits or try again.', 'error', 7000);
          } else {
            throw error;
          }
          return false;
        }
      }
    } catch (error) {
      console.error('Error in completePendingCardOrder:', error);
      
      // Show error notification to user
      showNotification('There was a problem sending your card. Please try again from the dashboard.', 'error', 7000);
      
      return false;
    }
  };
  
  // 5. Stripe integration functions
  
  // Buy credits function
  const buyCredits = async (priceId: string) => {
    try {
      // If we're sending a card, store its data before redirect
      let pendingCardData = null;
      
      // Check for pending card in the form data or local state
      if (selectedContact && showCardSelector) {
        pendingCardData = {
          recipientName: selectedContact.name,
          recipientAddress: {
            address_line1: selectedContact.address_line1,
            address_city: selectedContact.address_city,
            address_state: selectedContact.address_state,
            address_zip: selectedContact.address_zip,
            address_country: selectedContact.address_country || 'US'
          },
          contactId: selectedContact.id,
          message: (document.querySelector('#message-input') as HTMLTextAreaElement)?.value || '',
          occasion: selectedOccasion,
          cardImage: (document.querySelector('#selected-card-image') as HTMLImageElement)?.src || ''
        };
      }
      
      // Build a success URL with the right parameters
      let successUrl = window.location.origin + window.location.pathname;
      
      if (pendingCardData) {
        // Store the pending card data in localStorage with unique ID
        const pendingCardId = Date.now().toString();
        localStorage.setItem(`pendingCard_${pendingCardId}`, JSON.stringify(pendingCardData));
        
        // Add the pendingCard identifier to the success URL
        successUrl += `?pendingCard=${pendingCardId}&success=true&session_id={CHECKOUT_SESSION_ID}`;
      } else {
        // Standard success URL without pending card data
        successUrl += `?success=true&session_id={CHECKOUT_SESSION_ID}`;
      }
      
      await createCheckoutSession('one-time', priceId, successUrl);
    } catch (error) {
      console.error('Error buying credits:', error);
      showNotification('Failed to initiate checkout. Please try again.', 'error');
    }
  };
  
  // Subscribe to monthly plan function
  const subscribeToMonthly = async (priceId: string) => {
    try {
      // Build a success URL with query parameters that can handle the redirect smoothly
      const successUrl = window.location.origin + window.location.pathname + '?success=true&subscription=true&session_id={CHECKOUT_SESSION_ID}';
      
      await createCheckoutSession('subscription', priceId, successUrl);
    } catch (error) {
      console.error('Error creating subscription:', error);
      showNotification('Failed to initiate subscription checkout. Please try again.', 'error');
    }
  };
  
  // 6. Effect hooks
  
  // Initialize app data when user is available
  useEffect(() => {
    if (user) {
      const initializeApp = async () => {
        try {
          if (user) {
            // Check user profile
            try {
              const { data: profile, error } = await supabase
                .from('users')
                .select('*')
                .eq('user_id', user.id)
                .single();
                
              if (error || !profile || !profile.first_name || !profile.last_name || !profile.signature_url) {
                setShowProfileEdit(true);
              }
              setUserProfile(profile);
            } catch (error) {
              console.error('Error checking user profile:', error);
            }
            
            // Fetch contacts
            try {
              const contacts = await getContacts(user.id);
              setContacts(contacts);
            } catch (error) {
              console.error('Error fetching contacts:', error);
            }
            
            // Fetch user credits
            try {
              const { data, error } = await supabase.rpc('get_user_credits_with_expiration');
              
              if (error) throw error;
              
              if (data && Array.isArray(data) && data.length > 0) {
                const creditInfo = data[0];
                setUserCredits(creditInfo.total_credits || 0);
                setCreditDetails(creditInfo.purchases || []);
              }
            } catch (error) {
              console.error('Error fetching user credits:', error);
            }
            
            // Fetch subscription status (single place in the app)
            try {
              const status = await getSubscriptionStatus();
              setSubscriptionStatus(status);
            } catch (error) {
              console.error('Error fetching subscription status:', error);
            }
          }
        } catch (error) {
          console.error('Error initializing app:', error);
        }
      };
      initializeApp();
    }
  }, [user]);
  
  // Add back the Stripe return check effect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasStripeParams = params.get('session_id') !== null && params.get('success') === 'true';
    
    if (hasStripeParams) {
      const sessionId = params.get('session_id');
      console.log('Detected return from Stripe checkout with session:', sessionId);
      
      // Use sessionStorage to prevent repeated processing on the same page
      const processedKey = `stripe_return_processed_${sessionId}`;
      
      if (!sessionStorage.getItem(processedKey)) {
        console.log('Processing Stripe return for the first time');
        sessionStorage.setItem(processedKey, 'true');
        
        // Wait a moment to ensure all hooks are initialized
        setTimeout(() => {
          checkStripeReturn();
        }, 500);
      } else {
        console.log('Stripe return was already processed for this session');
      }
    }
  }, [checkStripeReturn, user]);
  
  // Fix the real-time subscription for credit updates
  useEffect(() => {
    if (!user) return;

    // Only set up subscription once
    let isSubscribed = true;
    let subscription: ReturnType<typeof supabase.channel> | null = null;
    
    const setupSubscription = async () => {
      try {
        subscription = supabase
          .channel('credit_updates')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'credit_updates',
              filter: `user_id=eq.${user.id}`
            },
            (payload) => {
              if (!isSubscribed) return;
              
              console.log('Credit update received:', payload);
              if (payload.new.credits_data && Array.isArray(payload.new.credits_data)) {
                const creditInfo = payload.new.credits_data[0];
                setUserCredits(creditInfo.total_credits || 0);
                setCreditDetails(creditInfo.purchases || []);
              }
            }
          )
          .subscribe();
      } catch (error) {
        console.error('Error setting up supabase channel subscription:', error);
      }
    };

    setupSubscription();

    return () => {
      isSubscribed = false;
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing from channel:', error);
        }
      }
    };
  }, [user?.id]); // Only depend on user ID

  // 7. UI interaction functions
  
  async function handleSendCard(contact: Contact, defaultOccasion?: CardOccasion) {
    // Check if contact has complete address
    if (!contact.address_line1 || !contact.address_city || 
        !contact.address_state || !contact.address_zip) {
      setSelectedContact(contact);
      setShowEditForm(true);
      return;
    }

    setSelectedContact(contact);
    if (defaultOccasion) {
      // If called from Events modal with a default occasion
      handleOccasionSelect(defaultOccasion);
    } else {
      // If called from dashboard, show the occasion selector
      setShowOccasionSelector(true);
    }
  }

  const handleOccasionSelect = (occasion: CardOccasion) => {
    setSelectedOccasion(occasion);
    setShowOccasionSelector(false);
    setShowCardSelector(true);
  };

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    const password = (form.elements.namedItem('password') as HTMLInputElement).value;
    await signIn(email, password);
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    const password = (form.elements.namedItem('password') as HTMLInputElement).value;
    const firstName = (form.elements.namedItem('first_name') as HTMLInputElement).value;
    const lastName = (form.elements.namedItem('last_name') as HTMLInputElement).value;

    const { user, error } = await signUp(email, password);
    if (error) {
      console.error('Error signing up:', error);
      return;
    }

    if (user) {
      await supabase.from('users').insert({
        user_id: user.id,
        first_name: firstName,
        last_name: lastName,
        email,
        signature_url: null
      });
    } else {
      console.error('User is null');
    }
  }

  async function handleCardSelect(cardId: string, message: string) {
    if (!selectedContact || !user) return;

    try {
      // Show a notification that the card is being processed
      showNotification('Processing your card, please wait...', 'success', 3000);
      
      // If cardId is a URL to an image, it's ready to use
      // Otherwise, treat it as HTML that needs to be converted to an image URL
      
      // Prepare the order data structure
      const orderData: CardOrderParams = {
        cardImage: cardId,
        message,
        occasion: (selectedOccasion || 'general') as CardOccasion,
        recipientName: selectedContact.name,
        recipientAddress: {
          name: selectedContact.name,
          address_line1: selectedContact.address_line1 || '',
          address_city: selectedContact.address_city || '',
          address_state: selectedContact.address_state || '',
          address_zip: selectedContact.address_zip || '',
          address_country: selectedContact.address_country || 'US'
        },
        contactId: selectedContact.id,
        signatureUrl: null
      };
      
      // Check if this is an image URL or HTML content
      const isImageUrl = cardId.match(/\.(jpe?g|png|gif|webp)$/i) !== null;
      
      if (isImageUrl) {
        // Direct image URL from CardSelector - we can process immediately
        const result = await processCardOrder(orderData);

        if (result?.success) {
          showNotification('Your physical card has been scheduled for delivery!', 'success');
        } else {
          showNotification('There was a problem sending your card. Please try again.', 'error');
        }
      } else {
        // Handle HTML to be rendered - redirect to CardEditor for customization
        // This branch shouldn't be reached if CardSelector is properly configured
        setShowCardSelector(false);
        // Could redirect to CardEditor here if needed
        showNotification('Please customize your card before sending.', 'error');
        return;
      }
      
      // Clean up UI state
      setShowCardSelector(false);
      setSelectedContact(null);
      
      // Refresh data to show the new order
      await refreshOrderHistory();
      await fetchUserCredits();
      
    } catch (error) {
      console.error('Error in handleCardSelect:', error);
      showNotification('Error processing card. Please try again.', 'error');
    }
  }

  async function checkUserProfile() {
    if (!user) return;
    const { data: profile, error } = await supabase
      .from('users')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error || !profile || !profile.first_name || !profile.last_name || !profile.signature_url) {
      setShowProfileEdit(true);
    }
    setUserProfile(profile);
  }

  // Add a function to format the date
  function formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }

  // Add subscription management functions
  const handleSubscriptionSelect = async (tier: string) => {
    try {
      await createCheckoutSession('subscription', tier);
      // No need to close modal as we're redirecting to Stripe
    } catch (error) {
      console.error('Error creating subscription:', error);
      // Show error message using our notification helper
      showNotification('Failed to start subscription process. Please try again.', 'error');
    }
  };

  const handleManageSubscription = async () => {
    try {
      if (hasActiveSubscription(subscriptionStatus)) {
        await redirectToCustomerPortal();
      } else {
        // Close the EditProfile modal first
        setShowProfileEdit(false);
        // Then show the subscription tiers modal
        setShowSubscriptionTiers(true);
      }
    } catch (error) {
      console.error('Error managing subscription:', error);
      // If portal creation fails, show subscription tiers as a fallback
      // Close the EditProfile modal first
      setShowProfileEdit(false);
      setShowSubscriptionTiers(true);
      
      // Show error message
      showNotification('Unable to access subscription portal. Please try again.', 'error');
    }
  };

  // Add a function to handle deep link parameters
  const handleDeepLink = useCallback(async () => {
    if (!user) return;

    const params = new URLSearchParams(window.location.search);
    const eventType = params.get('event_type');
    const contactId = params.get('contact_id');
    const occasion = params.get('occasion');

    if (eventType && contactId && occasion) {
      try {
        // Fetch the contact
        const { data: contact, error } = await supabase
          .from('contacts')
          .select('*')
          .eq('id', contactId)
          .eq('user_id', user.id)
          .single();

        if (error) throw error;
        if (!contact) throw new Error('Contact not found');

        // Start the card sending flow
        await handleSendCard(contact, occasion as CardOccasion);

        // Clean up URL parameters
        const url = new URL(window.location.href);
        url.search = '';
        window.history.replaceState({}, document.title, url.toString());
      } catch (error) {
        console.error('Error handling deep link:', error);
        showNotification('There was a problem loading your card. Please try again from the dashboard.', 'error');
      }
    }
  }, [user]);

  // Add effect to handle deep links and state
  useEffect(() => {
    const handleInitialState = () => {
      const state = window.history.state?.usr;
      if (state?.openUpcomingEvents) {
        setShowUpcomingEvents(true);
      }
    };

    handleInitialState();
    handleDeepLink();
  }, [handleDeepLink]);

  // Wrap the main content in Routes
  return (
    <Routes>
      <Route 
        path="/reset-password" 
        element={<ResetPasswordPage />} 
      />
      <Route 
        path="/template-test" 
        element={
          <ProtectedRoute adminOnly={true}>
            <TemplateTest />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/upcoming-events"
        element={
          <ProtectedRoute>
            <Navigate to="/" replace state={{ openUpcomingEvents: true }} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/send"
        element={
          <ProtectedRoute>
            <div className="min-h-screen brand-gradient">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Content will be loaded by handleDeepLink */}
                <header className="text-center mb-8 md:mb-12">
                  <div className="flex items-center justify-center mb-4">
                    <Mail className="w-10 h-10 sm:w-12 sm:h-12 text-brand-primary" />
                  </div>
                  <h1 className="text-4xl sm:text-5xl font-bold font-display text-brand-secondary mb-2">Cardible</h1>
                  <p className="text-lg sm:text-xl tagline text-brand-primary mb-4">Personalized cards, effortlessly delivered</p>
                </header>
              </div>
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path="/*"
        element={
          !user ? (
            // Login/Signup page
            <div className="min-h-screen brand-gradient flex items-center justify-center">
              <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
                <div className="flex items-center justify-center mb-8">
                  <Mail className="w-12 h-12 text-brand-primary" />
                </div>
                <h1 className="text-3xl font-bold font-display text-brand-secondary text-center mb-2">Cardible</h1>
                <p className="text-xl tagline text-brand-primary text-center mb-8">Personalized cards, effortlessly delivered</p>
                
                <div className="space-y-4">
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        autoComplete="username"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">Password</label>
                      <input
                        type="password"
                        name="password"
                        required
                        autoComplete="current-password"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="mt-1 text-sm text-brand-primary hover:text-brand-secondary transition-colors"
                      >
                        Forgot your password?
                      </button>
                    </div>
                    {authError && (
                      <p className="text-red-500 text-sm">{authError}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-brand-secondary transition-colors"
                    >
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </button>
                  </form>

                  {/* Forgot Password Modal */}
                  {showForgotPassword && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-xl font-semibold font-display text-brand-secondary">Reset Password</h3>
                          <button
                            onClick={() => setShowForgotPassword(false)}
                            className="text-brand-secondary hover:text-brand-primary"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        
                        {resetEmailSent ? (
                          <div className="text-center py-4">
                            <div className="mb-4 text-green-600">
                              <Check className="w-12 h-12 mx-auto" />
                            </div>
                            <p className="text-brand-secondary mb-4">
                              Password reset instructions have been sent to your email.
                              Please check your inbox and follow the link to reset your password.
                            </p>
                            <button
                              onClick={() => {
                                setShowForgotPassword(false);
                                setResetEmailSent(false);
                              }}
                              className="text-brand-primary hover:text-brand-secondary transition-colors"
                            >
                              Return to login
                            </button>
                          </div>
                        ) : (
                          <form
                            onSubmit={async (e) => {
                              e.preventDefault();
                              const email = (e.target as HTMLFormElement).email.value;
                              await resetPassword(email);
                            }}
                            className="space-y-4"
                          >
                            <p className="text-brand-secondary mb-4">
                              Enter your email address and we'll send you instructions to reset your password.
                            </p>
                            <div>
                              <label className="block text-sm font-medium text-brand-secondary mb-1">
                                Email Address
                              </label>
                              <input
                                type="email"
                                name="email"
                                required
                                autoComplete="username"
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                              />
                            </div>
                            {authError && (
                              <p className="text-red-500 text-sm">{authError}</p>
                            )}
                            <div className="flex justify-end gap-3">
                              <button
                                type="button"
                                onClick={() => setShowForgotPassword(false)}
                                className="px-4 py-2 text-brand-secondary hover:text-brand-primary transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
                              >
                                Send Reset Link
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-brand-secondary">Or</span>
                    </div>
                  </div>

                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        autoComplete="username"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">Password</label>
                      <input
                        type="password"
                        name="password"
                        required
                        autoComplete="new-password"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 bg-white text-brand-primary px-4 py-2 rounded-lg border border-brand-primary hover:bg-brand-accent transition-colors"
                    >
                      <UserPlus className="w-4 h-4" />
                      Sign Up
                    </button>
                  </form>
                </div>
              </div>
            </div>
          ) : (
            // Main app
            <div className="min-h-screen brand-gradient">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <header className="text-center mb-8 md:mb-12">
                  <div className="flex items-center justify-center mb-4">
                    <Mail className="w-10 h-10 sm:w-12 sm:h-12 text-brand-primary" />
                  </div>
                  <h1 className="text-4xl sm:text-5xl font-bold font-display text-brand-secondary mb-2">Cardible</h1>
                  <p className="text-lg sm:text-xl tagline text-brand-primary mb-4">Personalized cards, effortlessly delivered</p>
                  
                  <div className="mt-4 flex items-center justify-between w-full max-w-4xl mx-auto relative">
                    {/* Menu Button - Left */}
                    <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="text-brand-primary hover:text-brand-secondary transition-colors"
                    >
                      <Menu className="w-6 h-6" />
                    </button>

                    {/* Calendar Icon - Center */}
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <button
                        onClick={() => setShowUpcomingEvents(true)}
                        className="text-brand-primary hover:text-brand-secondary transition-colors"
                      >
                        <Calendar className="w-6 h-6" />
                      </button>
                    </div>

                    {/* Credits Display - Right */}
                    <button
                      onClick={() => setShowCreditDetails(true)}
                      className="text-brand-primary hover:text-brand-secondary transition-colors"
                    >
                      <span className="text-sm sm:text-base">{userCredits} Credits</span>
                    </button>
                  </div>
                </header>

                <Sidebar 
                  isOpen={isMenuOpen} 
                  onClose={() => setIsMenuOpen(false)}
                  setShowProfileEdit={setShowProfileEdit}
                  setShowOrderHistory={setShowOrderHistory}
                  fetchOrderHistory={fetchOrderHistory}
                  setShowCreditBundles={setShowCreditBundles}
                  logout={logout}
                />

                <main className="space-y-6 md:space-y-8">
                  {/* Contacts Section */}
                  <div className="card">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                      <h2 className="text-xl sm:text-2xl font-semibold font-display text-brand-secondary">Your Contacts</h2>
                      <button
                        onClick={() => setShowAddForm(true)}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-brand-secondary transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-sm sm:text-base">Add Contact</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {contacts.map((contact) => (
                        <div
                          key={contact.id}
                          onClick={() => {
                            setSelectedContact(contact);
                            setShowEditForm(true);
                          }}
                          className="bg-white rounded-lg p-4 shadow hover:shadow-md transition-shadow cursor-pointer"
                        >
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <h3 className="font-semibold text-base sm:text-lg text-brand-secondary">{contact.name}</h3>
                              <p className="text-brand-secondary text-sm">{contact.email}</p>
                              <div className="mt-1 text-sm text-brand-secondary opacity-75">
                                <p>{contact.relationship} • Prefers {contact.card_preference} cards</p>
                                {(!contact.address_line1 || !contact.address_city || 
                                  !contact.address_state || !contact.address_zip) && (
                                  <p className="text-red-500 mt-1">Missing mailing address</p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {contact.birthday && (
                                <div title={`Birthday: ${new Date(contact.birthday).toLocaleDateString()}`} className="p-1">
                                  <Gift className="w-5 h-5 text-brand-primary" />
                                </div>
                              )}
                              {contact.anniversary && (
                                <div title={`Anniversary: ${new Date(contact.anniversary).toLocaleDateString()}`} className="p-1">
                                  <Heart className="w-5 h-5 text-brand-primary" />
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-center mt-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendCard(contact);
                              }}
                              className="px-4 py-2 text-white bg-brand-primary hover:bg-brand-secondary transition-colors rounded-lg"
                            >
                              Send Card
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </main>

                {/* Modals */}
                {showCardSelector && selectedContact && (
                  <CardSelector
                    occasion={selectedOccasion}
                    contact={selectedContact}
                    onSelect={handleCardSelect}
                    onClose={() => {
                      setShowCardSelector(false);
                      setSelectedContact(null);
                    }}
                    subscriptionStatus={subscriptionStatus}
                    userCredits={userCredits}
                    onApproveCard={async (data) => {
                      try {
                        // data contains front and back imageUrls and recipient data
                        showNotification('Processing your card, please wait...', 'success', 3000);
                        
                        // Check if we have valid image URLs
                        if (data.front && data.back && data.to && data.to.name) {
                          const result = await processCardOrder({
                            cardImage: "card generated in CardEditor", // Reference only
                            message: "message embedded in card", // The message is already embedded in the back image
                            occasion: selectedOccasion || 'general',
                            recipientName: data.to.name,
                            recipientAddress: data.to,
                            frontImageUrl: data.front,
                            backImageUrl: data.back,
                            renderMode: 'remote_urls',
                            contactId: selectedContact.id
                          });
                          
                          if (result?.success) {
                            showNotification('Your physical card has been scheduled for delivery!', 'success');
                            setShowCardSelector(false);
                            setSelectedContact(null);
                            // Refresh data
                            await refreshOrderHistory();
                            await fetchUserCredits();
                          } else {
                            showNotification(result?.error || 'Failed to send card', 'error');
                          }
                        } else {
                          showNotification('Missing card image data. Please try again.', 'error');
                        }
                      } catch (error) {
                        console.error('Error in onApproveCard:', error);
                        showNotification('Error sending card. Please try again.', 'error');
                      }
                    }}
                  />
                )}

                {showAddForm && (
                  <ContactForm
                    mode="add"
                    onClose={() => setShowAddForm(false)}
                    onSubmit={() => {
                      setShowAddForm(false);
                      fetchContacts();
                    }}
                  />
                )}

                {showEditForm && selectedContact && (
                  <ContactForm
                    mode="edit"
                    contact={selectedContact}
                    onClose={() => {
                      setShowEditForm(false);
                      setSelectedContact(null);
                    }}
                    onSubmit={() => {
                      setShowEditForm(false);
                      setSelectedContact(null);
                      fetchContacts();
                    }}
                  />
                )}

                {showCreditBundles && (
                  <CreditBundles
                    onClose={() => {
                      setShowCreditBundles(false);
                      fetchUserCredits();
                    }}
                    onSelectBundle={async (bundle) => {
                      try {
                        await createCheckoutSession('one-time', bundle);
                        // Note: No need to close modal here as we're redirecting to Stripe
                        // The modal will be closed when we return from Stripe
                      } catch (error) {
                        console.error('Error creating checkout session:', error);
                        // Use a more user-friendly error display
                        const errorMessage = document.createElement('div');
                        errorMessage.className = 'fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg';
                        errorMessage.innerHTML = 'Failed to start checkout process. Please try again.';
                        document.body.appendChild(errorMessage);
                        setTimeout(() => errorMessage.remove(), 5000);
                      }
                    }}
                  />
                )}

                {showSubscriptionTiers && (
                  <SubscriptionTiers
                    onClose={() => setShowSubscriptionTiers(false)}
                    onSelectTier={handleSubscriptionSelect}
                  />
                )}

                {showProfileEdit && userProfile && (
                  <EditProfile
                    user={userProfile}
                    subscriptionStatus={subscriptionStatus}
                    onClose={() => {
                      setShowProfileEdit(false);
                      fetchUserCredits();
                    }}
                    onUpdate={() => {
                      setShowProfileEdit(false);
                      fetchContacts();
                      fetchUserCredits();
                      checkUserProfile();
                    }}
                    onManageSubscription={handleManageSubscription}
                    showNotification={showNotification}
                  />
                )}

                {showOrderHistory && (
                  <OrderHistoryModal
                    orderHistory={orderHistory}
                    orderHistoryLoading={orderHistoryLoading}
                    orderHistoryPage={orderHistoryPage}
                    orderHistoryTotal={orderHistoryTotal}
                    ORDER_HISTORY_LIMIT={ORDER_HISTORY_LIMIT}
                    onClose={() => setShowOrderHistory(false)}
                    onPageChange={(page) => {
                      setOrderHistoryPage(page);
                      fetchOrderHistory(page);
                    }}
                    onSendFirstCard={() => {
                      setShowOrderHistory(false);
                      if (contacts.length > 0) {
                        setSelectedContact(contacts[0]);
                        setShowCardSelector(true);
                      } else {
                        setShowAddForm(true);
                      }
                    }}
                  />
                )}

                {showCreditDetails && (
                  <CreditDetailsModal
                    userCredits={userCredits}
                    creditDetails={creditDetails}
                    onClose={() => setShowCreditDetails(false)}
                    onPurchaseCredits={() => setShowCreditBundles(true)}
                  />
                )}

                {showUpcomingEvents && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                      <div className="flex justify-between items-center p-4 border-b">
                        <h3 className="text-xl font-semibold text-brand-secondary flex items-center gap-2">
                          <Calendar className="w-5 h-5" />
                          Upcoming Events
                        </h3>
                        <button
                          onClick={() => setShowUpcomingEvents(false)}
                          className="text-brand-secondary hover:text-brand-primary transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="p-4">
                        <UpcomingEvents
                          onSelectEvent={(event) => {
                            const contact = contacts.find(c => c.id === event.contactId);
                            if (contact) {
                              handleSendCard(contact, event.occasion);
                            }
                            setShowUpcomingEvents(false);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Add OccasionSelectorModal */}
                <OccasionSelectorModal
                  isOpen={showOccasionSelector}
                  onClose={() => setShowOccasionSelector(false)}
                  onSelect={handleOccasionSelect}
                  defaultOccasion={selectedOccasion || undefined}
                  contact={selectedContact || undefined}
                />
              </div>
            </div>
          )
        }
      />
    </Routes>
  );
}

